import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, CreditCard, Loader2, ArrowRight, MapPin, BarChart3, FileText, Leaf, ClipboardList, Eye, Bell, Sparkles, Calculator, Star, Lock, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { authFetch } from "@/lib/api";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");

  const [subStatus, setSubStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    authFetch(`${API_BASE}/stripe/subscription-status`)
      .then(r => r.json())
      .then(setSubStatus)
      .catch(() => null)
      .finally(() => setLoading(false));
  }, []);

  const pollStatus = useCallback(async (sid, attempt) => {
    if (attempt >= 8) { toast.error("Verification du paiement expiree. Veuillez rafraichir."); return; }
    try {
      const res = await authFetch(`${API_BASE}/stripe/checkout-status/${sid}`);
      const data = await res.json();
      if (data.payment_status === "paid") {
        toast.success("Paiement reussi ! Bienvenue sur Veyer CIEL");
        setTimeout(() => navigate("/dashboard"), 1500);
        return;
      }
      if (data.status === "expired") { toast.error("Session expiree"); return; }
      setPollCount(attempt + 1);
      setTimeout(() => pollStatus(sid, attempt + 1), 2500);
    } catch { setTimeout(() => pollStatus(sid, attempt + 1), 3000); }
  }, [navigate]);

  useEffect(() => { if (sessionId) pollStatus(sessionId, 0); }, [sessionId, pollStatus]);

  const [checkoutSessionId, setCheckoutSessionId] = useState(null);
  const [waitingPayment, setWaitingPayment] = useState(false);

  const pollCheckoutSession = useCallback(async (sid, attempt) => {
    if (attempt >= 60) { setWaitingPayment(false); toast.error("Verification expiree. Veuillez rafraichir."); return; }
    try {
      const res = await authFetch(`${API_BASE}/stripe/checkout-status/${sid}`);
      const data = await res.json();
      if (data.payment_status === "paid" || data.payment_status === "no_payment_required") {
        toast.success("Paiement reussi ! Bienvenue sur Veyer CIEL");
        setWaitingPayment(false);
        setTimeout(() => navigate("/dashboard"), 1500);
        return;
      }
      if (data.status === "expired") { setWaitingPayment(false); toast.error("Session expiree"); return; }
      setTimeout(() => pollCheckoutSession(sid, attempt + 1), 3000);
    } catch { setTimeout(() => pollCheckoutSession(sid, attempt + 1), 4000); }
  }, [navigate]);

  const handleCheckout = async (planId) => {
    setCheckoutLoading(planId);
    try {
      const res = await authFetch(`${API_BASE}/stripe/create-checkout-session`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origin_url: window.location.origin, plan_id: planId }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || "Erreur"); }
      const data = await res.json();
      if (data.url) {
        // Open Stripe checkout in new tab to avoid iframe restrictions
        const stripeWindow = window.open(data.url, "_blank");
        if (!stripeWindow || stripeWindow.closed) {
          // Popup blocked — fallback: show direct link
          setCheckoutSessionId(data.session_id);
          setCheckoutLoading(null);
          toast.info("Cliquez sur le lien ci-dessous pour acceder au paiement");
          // Store URL for fallback link
          setCheckoutUrl(data.url);
          return;
        }
        // Start polling for payment completion
        setWaitingPayment(true);
        setCheckoutLoading(null);
        pollCheckoutSession(data.session_id, 0);
        return;
      }
      throw new Error("Pas d'URL de paiement");
    } catch (e) {
      setCheckoutLoading(null);
      toast.error(e.message || "Erreur de paiement");
    }
  };

  const [checkoutUrl, setCheckoutUrl] = useState(null);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0A0A0A" }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: "#2D6A4F" }} />
      </div>
    );
  }

  if (waitingPayment || sessionId) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0A0A0A" }}>
        <div className="text-center space-y-4 p-8 max-w-md">
          <Loader2 className="w-12 h-12 animate-spin mx-auto" style={{ color: "#2D6A4F" }} />
          <h2 className="text-xl font-bold text-white" style={{ fontFamily: "Manrope, sans-serif" }}>
            {waitingPayment ? "Paiement en cours..." : "Verification du paiement..."}
          </h2>
          <p className="text-sm" style={{ color: "#7A7A7A" }}>
            {waitingPayment
              ? "La page de paiement Stripe s'est ouverte dans un nouvel onglet. Completez le paiement puis revenez ici."
              : `Tentative ${pollCount + 1}/8`}
          </p>
          {checkoutUrl && (
            <a href={checkoutUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white mt-4"
              style={{ background: "#2D6A4F" }} data-testid="stripe-fallback-link">
              <CreditCard className="w-4 h-4" /> Ouvrir la page de paiement
              <ArrowRight className="w-4 h-4" />
            </a>
          )}
          <div className="pt-4">
            <button onClick={() => { setWaitingPayment(false); setCheckoutUrl(null); }}
              className="text-xs underline" style={{ color: "#7A7A7A" }} data-testid="cancel-waiting-btn">
              Annuler
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (subStatus?.has_access) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "#0A0A0A" }}>
        <div className="max-w-md w-full space-y-6 text-center">
          <CheckCircle className="w-16 h-16 mx-auto" style={{ color: "#2D6A4F" }} />
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "Manrope, sans-serif" }}>Abonnement actif</h1>
          <p className="text-sm" style={{ color: "#7A7A7A" }}>
            {subStatus.is_admin ? "Accès administrateur illimité" : "Abonnement Véyèr CIEL"}
          </p>
          <Button className="w-full h-12 text-base font-semibold rounded-xl text-white" style={{ background: "#2D6A4F" }}
            onClick={() => navigate("/dashboard")} data-testid="go-to-dashboard-btn">
            Accéder à la carte
          </Button>
        </div>
      </div>
    );
  }

  const subFeatures = [
    { icon: Lock, text: "Verrouillage jusqu'à 5 parcelles" },
    { icon: ClipboardList, text: "Carnet PRO complet (Phyto, Bio, Fertilisation, Sol, Sanitaire)" },
    { icon: FileText, text: "Registre phytosanitaire numérique conforme" },
    { icon: Bell, text: "Vigie Sanitaire proactive (72 bioagresseurs)" },
    { icon: Sparkles, text: "Import IA (facture, bon traitement, analyse sol)" },
    { icon: Calculator, text: "Fertilisation : connexion à AMENDA (CIRAD) en préparation" },
    { icon: Users, text: "3 collaborateurs gratuits / parcelle" },
    { icon: Eye, text: "Mode contrôle DAAF/SRAL 24h" },
  ];

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#0A0A0A" }} data-testid="subscription-page">
      {/* Header */}
      <div className="pt-8 pb-4 text-center">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white" style={{ fontFamily: "Manrope, sans-serif" }}>
          Un seul abonnement — tout inclus
        </h1>
        <p className="text-sm mt-2" style={{ color: "#7A7A7A" }}>Biomyr — La Réunion &amp; Mayotte</p>
      </div>

      {/* Plan Card */}
      <div className="flex-1 flex items-start justify-center px-4 pb-8">
        <div className="max-w-md w-full">
          <div className="rounded-2xl border-2 p-6 space-y-5 relative overflow-hidden" style={{ borderColor: "#2D6A4F", background: "#111111" }}
            data-testid="plan-card-sub">
            <div className="absolute top-3 right-3 px-3 py-1 rounded-full text-[10px] font-bold text-white" style={{ background: "#2D6A4F" }}>
              5 JOURS GRATUITS
            </div>
            <div className="flex items-center justify-center py-6">
              <div className="w-24 h-24 rounded-2xl flex items-center justify-center" style={{ background: "#2D6A4F18" }}>
                <Leaf className="w-12 h-12" style={{ color: "#2D6A4F" }} />
              </div>
            </div>
            <div className="text-center">
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: "Manrope, sans-serif" }}>Véyèr CIEL</h2>
              <p className="text-xs mt-1" style={{ color: "#7A7A7A" }}>Accès complet + verrouillage 5 parcelles maximum</p>
            </div>
            <div className="space-y-2.5 py-2">
              {subFeatures.map((f) => (
                <div key={f.text} className="flex items-center gap-2.5">
                  <f.icon className="w-4 h-4 shrink-0" style={{ color: "#2D6A4F" }} />
                  <span className="text-sm text-white/80">{f.text}</span>
                </div>
              ))}
            </div>
            <Button className="w-full h-12 text-sm font-semibold rounded-xl text-white gap-2"
              style={{ background: "#2D6A4F" }}
              onClick={() => handleCheckout("veyer_ciel_sub")} disabled={!!checkoutLoading} data-testid="subscribe-sub-btn">
              {checkoutLoading === "veyer_ciel_sub" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              Démarrer l'essai gratuit (5 jours)
              <ArrowRight className="w-4 h-4 ml-auto" />
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="pb-6 text-center">
        <p className="text-[11px]" style={{ color: "#555" }}>Paiement sécurisé via Stripe. Annulation possible à tout moment.</p>
      </div>
    </div>
  );
}
